import { useEffect, useState } from 'react';

interface HealthCheck {
  status: string;
  latencyMs?: number;
}

interface HealthResponse {
  status: string;
  version: string;
  uptimeSeconds: number;
  checks: Record<string, HealthCheck>;
}

export function HealthCheck() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/health')
      .then((res) => res.json())
      .then((data: HealthResponse) => {
        setHealth(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold bg-brand-gradient bg-clip-text text-transparent mb-6">
          UniTicket
        </h1>
        <h2 className="text-lg font-semibold text-slate-700 mb-4">API Health Check</h2>

        {loading && <p className="text-slate-500">Connecting to API...</p>}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 font-medium">Connection failed</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        )}

        {health && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                className={`w-3 h-3 rounded-full ${
                  health.status === 'healthy'
                    ? 'bg-green-500'
                    : health.status === 'degraded'
                      ? 'bg-amber-500'
                      : 'bg-red-500'
                }`}
              />
              <span className="font-medium text-slate-700 capitalize">{health.status}</span>
            </div>
            <div className="text-sm text-slate-500 space-y-1">
              <p>Version: {health.version}</p>
              <p>Uptime: {health.uptimeSeconds}s</p>
              {Object.entries(health.checks).map(([name, check]) => (
                <p key={name}>
                  {name}: {check.status}
                  {check.latencyMs != null && ` (${check.latencyMs}ms)`}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
